import {
  useEffect,
  useRef,
  memo,
  forwardRef,
  useImperativeHandle,
  useCallback
} from 'react'
import { Box, CircularProgress } from '@mui/material'

/**
 * INITIAL_BATCH — how many chunks to render on first load.
 * BATCH_SIZE    — how many chunks to append when user scrolls near bottom.
 * SENTINEL_MARGIN — IntersectionObserver rootMargin (trigger before reaching bottom).
 */
const INITIAL_BATCH = 20
const BATCH_SIZE = 15
const SENTINEL_MARGIN = '600px'

/**
 * LazyIframe — renders large HTML documents inside an iframe with progressive
 * chunk injection. The iframe provides complete CSS isolation from MUI/app styles.
 * Chunks are injected in batches as the user scrolls, preventing the initial freeze.
 *
 * Ref exposes:
 *   - getIframeDocument() — the iframe's contentDocument
 *   - getIframeRef() — the iframe element itself
 *   - scrollToChunk(index) — scroll a chunk into view inside the iframe
 *   - scrollToArticle(articleNumber) — scroll to a [data-article] element
 *   - getLoadedCount() — how many chunks are currently in the DOM
 *   - loadAllChunks() — force-load all remaining chunks
 */
const LazyIframe = forwardRef(({
  chunks,
  styles,
  dir = 'ltr',
  onIframeReady
}, ref) => {
  const iframeRef = useRef(null)
  const loadedCountRef = useRef(0)
  const sentinelObserverRef = useRef(null)
  const isSettingUp = useRef(false)
  const chunksRef = useRef(chunks)

  // Keep chunksRef in sync so appendBatch callback always sees latest chunks
  useEffect(() => {
    chunksRef.current = chunks
  }, [chunks])

  /**
   * Append the next batch of chunks into the iframe body.
   */
  const appendBatch = useCallback((iframeDoc, batchSize = BATCH_SIZE) => {
    const currentChunks = chunksRef.current
    if (!iframeDoc || !currentChunks || currentChunks.length === 0) return 0

    const start = loadedCountRef.current
    const end = Math.min(start + batchSize, currentChunks.length)
    if (start >= end) return 0

    const container = iframeDoc.getElementById('chunk-container')
    if (!container) return 0

    // Remove sentinel before appending
    const existingSentinel = iframeDoc.getElementById('lazy-sentinel')
    if (existingSentinel) existingSentinel.remove()

    // Append chunks
    for (let i = start; i < end; i++) {
      const wrapper = iframeDoc.createElement('div')
      wrapper.setAttribute('data-chunk-id', currentChunks[i].id)
      wrapper.setAttribute('data-chunk-index', String(currentChunks[i].index))
      if (currentChunks[i].articleNumber) {
        wrapper.setAttribute('data-chunk-article', currentChunks[i].articleNumber)
      }
      wrapper.innerHTML = currentChunks[i].html
      container.appendChild(wrapper)
    }

    loadedCountRef.current = end

    // Add sentinel if there are more chunks to load
    if (end < currentChunks.length) {
      const sentinel = iframeDoc.createElement('div')
      sentinel.id = 'lazy-sentinel'
      sentinel.style.height = '1px'
      container.appendChild(sentinel)

      if (sentinelObserverRef.current) {
        sentinelObserverRef.current.observe(sentinel)
      }
    }

    return end - start
  }, [])

  /**
   * Set up the iframe: write the shell HTML, inject styles, load first batch,
   * set up IntersectionObserver for lazy loading.
   */
  const setupIframe = useCallback(() => {
    const iframe = iframeRef.current
    if (!iframe || !chunks || chunks.length === 0 || isSettingUp.current) return

    isSettingUp.current = true
    loadedCountRef.current = 0

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document
    if (!iframeDoc) {
      isSettingUp.current = false
      return
    }

    // Write the shell document with the original document styles
    iframeDoc.open()
    iframeDoc.write(`
      <!DOCTYPE html>
      <html dir="${dir}">
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        ${styles}
      </head>
      <body>
        <div id="chunk-container"></div>
      </body>
      </html>
    `)
    iframeDoc.close()

    // Set up IntersectionObserver inside the iframe for lazy loading
    const IframeIntersectionObserver = iframe.contentWindow?.IntersectionObserver
    if (IframeIntersectionObserver) {
      if (sentinelObserverRef.current) {
        sentinelObserverRef.current.disconnect()
      }

      sentinelObserverRef.current = new IframeIntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              sentinelObserverRef.current?.unobserve(entry.target)
              const iDoc = iframeRef.current?.contentDocument
              if (iDoc) appendBatch(iDoc)
            }
          }
        },
        { rootMargin: SENTINEL_MARGIN }
      )
    }

    // Load the first batch
    appendBatch(iframeDoc, INITIAL_BATCH)

    isSettingUp.current = false

    // Notify parent that iframe is ready
    if (onIframeReady) {
      onIframeReady(iframeDoc)
    }
  }, [chunks, styles, dir, appendBatch, onIframeReady])

  // Set up iframe when chunks/styles change
  useEffect(() => {
    if (!chunks || chunks.length === 0) return

    const timer = setTimeout(() => {
      setupIframe()
    }, 50)

    return () => {
      clearTimeout(timer)
      if (sentinelObserverRef.current) {
        sentinelObserverRef.current.disconnect()
        sentinelObserverRef.current = null
      }
    }
  }, [setupIframe])

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    getIframeDocument: () => {
      return iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document || null
    },
    getIframeRef: () => iframeRef.current,
    scrollToChunk: (index) => {
      const iframeDoc = iframeRef.current?.contentDocument
      if (!iframeDoc) return

      // Ensure the chunk is loaded
      if (index >= loadedCountRef.current) {
        const needed = index - loadedCountRef.current + BATCH_SIZE
        appendBatch(iframeDoc, needed)
      }

      const el = iframeDoc.querySelector(`[data-chunk-index="${index}"]`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    scrollToArticle: (articleNumber) => {
      const iframeDoc = iframeRef.current?.contentDocument
      if (!iframeDoc) return -1

      // Check if article is already loaded
      let el = iframeDoc.querySelector(`[data-article="${articleNumber}"]`)
      if (!el) {
        // Find which chunk has this article and load up to it
        const currentChunks = chunksRef.current
        const targetIdx = currentChunks.findIndex(c => c.articleNumber === String(articleNumber))
        if (targetIdx >= 0 && targetIdx >= loadedCountRef.current) {
          const needed = targetIdx - loadedCountRef.current + BATCH_SIZE
          appendBatch(iframeDoc, needed)
        }
        el = iframeDoc.querySelector(`[data-article="${articleNumber}"]`)
      }

      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        return chunksRef.current.findIndex(c => c.articleNumber === String(articleNumber))
      }
      return -1
    },
    getLoadedCount: () => loadedCountRef.current,
    loadAllChunks: () => {
      const iframeDoc = iframeRef.current?.contentDocument
      if (iframeDoc) {
        appendBatch(iframeDoc, chunksRef.current.length - loadedCountRef.current)
      }
    }
  }), [appendBatch])

  if (!chunks || chunks.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%'
        }}
      >
        <CircularProgress size={40} />
      </Box>
    )
  }

  return (
    <iframe
      ref={iframeRef}
      title="document-viewer"
      sandbox="allow-same-origin allow-scripts allow-popups"
      style={{
        border: 'none',
        width: '100%',
        height: '100%',
        display: 'block'
      }}
      dir={dir}
    />
  )
})

LazyIframe.displayName = 'LazyIframe'

export default memo(LazyIframe)
